import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createAiSummaryBulk } from '@/api/orders';
import type { AiBulkCreatePayload } from '@/api/orders/types';
import { internalOrderKeys } from './useInternalOrdersList';
import { purchaseOrderKeys } from './usePurchaseOrdersList';
import { salesOrderKeys } from './useSalesOrdersList';

// The bulk-summary endpoint only ENQUEUES the analysis — the AI-processing
// queue row is created moments later by the background worker. An invalidation
// fired right after the request can therefore still read an unchanged queue,
// and with no PROCESSING items both queue queries stop polling entirely, so
// the navbar badge would stay stale until an unrelated refetch. Re-check on
// these delays; once a refetch sees the PROCESSING row, the queue queries'
// own 10s polling takes over.
const QUEUE_RECHECK_DELAYS_MS = [4_000, 12_000];

export function useAiSummaryBulkCreate() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (payload: AiBulkCreatePayload) => createAiSummaryBulk(payload),
  });

  function invalidateQueue() {
    void queryClient.invalidateQueries({ queryKey: ['ai-processing-summary'] });
    void queryClient.invalidateQueries({ queryKey: ['ai-processing-stats'] });
  }

  return {
    isPending: mutation.isPending,
    mutateAsync: async (payload: AiBulkCreatePayload) => {
      const result = await mutation.mutateAsync(payload);
      void queryClient.invalidateQueries({ queryKey: ['aiSummary'] });
      void queryClient.invalidateQueries({ queryKey: salesOrderKeys.all });
      void queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      void queryClient.invalidateQueries({ queryKey: internalOrderKeys.all });
      invalidateQueue();
      for (const delay of QUEUE_RECHECK_DELAYS_MS) {
        // No cleanup needed: the query client outlives the component, and a
        // late invalidation of an inactive query is a harmless no-op.
        setTimeout(invalidateQueue, delay);
      }
      return result;
    },
  };
}
